import React, { useEffect } from "react";
import { Loading } from "./Loader";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchProductBreakdown,
  selectBreakdown,
  selectBreakdownStatus,
} from "../features/stockOverview/stockOverviewSlice";

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("ru-RU");
}

export default function StockBreakdownModal({ product, onClose }) {
  const dispatch = useDispatch();
  const productId = product?.id;
  const rows = useSelector(selectBreakdown(productId));
  const status = useSelector(selectBreakdownStatus(productId));

  useEffect(() => {
    if (productId != null) {
      dispatch(fetchProductBreakdown({ productId }));
    }
  }, [dispatch, productId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!product) return null;

  const threshold = product.effective_threshold;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.head}>
          <div>
            <div style={styles.title}>{product.name || "—"}</div>
            <div style={styles.sub}>
              Остатки по магазинам · порог: {threshold ?? "—"}
            </div>
          </div>
          <button type="button" onClick={onClose} style={styles.closeBtn} title="Закрыть">
            ✕
          </button>
        </div>

        <div style={styles.bodyWrap}>
          {status === "loading" && <Loading text="Загрузка…" />}
          {status === "failed" && <div style={styles.error}>Не удалось загрузить остатки</div>}
          {status === "succeeded" && rows.length === 0 && (
            <div style={styles.notice}>Нет остатков ни в одном магазине</div>
          )}
          {rows.length > 0 && (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Магазин</th>
                  <th style={styles.th}>Остаток</th>
                  <th style={styles.th}>Обновлено</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const qty = Number(r.quantity ?? 0);
                  const low = threshold != null && qty < threshold;
                  return (
                    <tr key={r.id}>
                      <td style={styles.td}>{r.store?.name || r.store_name || "—"}</td>
                      <td style={{ ...styles.tdMono, ...(low ? styles.low : null) }}>
                        {qty}
                        {low ? " ⚠" : ""}
                      </td>
                      <td style={styles.td}>{formatDateTime(r.updated_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 16,
  },
  modal: {
    width: 560,
    maxWidth: "100%",
    maxHeight: "80vh",
    display: "flex",
    flexDirection: "column",
    background: "var(--c-card)",
    color: "var(--c-text)",
    border: "1px solid var(--c-border)",
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
  },
  head: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    padding: "16px 20px",
    borderBottom: "1px solid var(--c-border)",
  },
  title: { fontSize: 19, fontWeight: 900 },
  sub: { marginTop: 3, fontSize: 14, color: "var(--c-text-muted)" },
  closeBtn: {
    width: 30,
    height: 30,
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text-sub)",
    cursor: "pointer",
    fontWeight: 700,
  },
  bodyWrap: { padding: 16, overflow: "auto" },
  notice: { padding: "12px 4px", fontSize: 15, color: "var(--c-text-muted)" },
  error: { padding: "12px 4px", fontSize: 15, color: "var(--c-danger)" },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    fontSize: 13,
    fontWeight: 700,
    color: "var(--c-text-sub)",
    background: "var(--c-input)",
    borderBottom: "1px solid var(--c-border)",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  td: { padding: "8px 10px", fontSize: 15, borderBottom: "1px solid var(--c-border)" },
  tdMono: {
    padding: "8px 10px",
    fontSize: 15,
    borderBottom: "1px solid var(--c-border)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  },
  low: { color: "var(--c-danger)", fontWeight: 800 },
};
